import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useDemoTranscriptQueries } from '../../../../../queries/Lead/useDemoTranscriptsQuery';
import { useLeadDemoRecordingQueries } from '../../../../../queries/Lead/useLeadDemoRecordingsQuery';
import { reactQueryClient } from '../../../../../web/ReactQueryProvider';
import { ILeadSearchView } from '../../../../viewModels/leads/interfaces';
import { buildSynthesisPrompt } from '../utils/buildSynthesisPrompt';
import { extractDemoContext } from '../utils/extractDemoContext';
import { AnalysisPhase, IndividualAnalysis } from './types';

interface UseMultiDemoSelectionAnalysisProps {
	selectedLeadSearchViews: ILeadSearchView[];
	source?: string;
	agentTemplateId?: string;
	concurrencyLimit?: number;
}

const SALES_DEMO_CALL_COACHING_AGENT = 'SalesDemoCallCoachingAgent';
const DEFAULT_CONCURRENCY = 1;

/**
 * Hook for analyzing multiple selected demos with hierarchical two-pass approach
 * Pass 1: Analyze each demo individually
 * Pass 2: Synthesize all individual analyses into a comprehensive report
 */
export const useMultiDemoSelectionAnalysis = ({
	selectedLeadSearchViews,
	source,
	agentTemplateId,
	concurrencyLimit = DEFAULT_CONCURRENCY,
}: UseMultiDemoSelectionAnalysisProps) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useMultiDemoSelectionAnalysis');

	// Core modal state
	const [isAnalysisModalOpen, setIsAnalysisModalOpen] = React.useState(false);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);
	const [isLoadingTranscriptions, setIsLoadingTranscriptions] = React.useState<boolean>(false);
	const [isNewAnalysisSession, setIsNewAnalysisSession] = React.useState<boolean>(true);
	const [shouldFetchData, setShouldFetchData] = React.useState<boolean>(false);

	// Hierarchical analysis state
	const [analysisPhase, setAnalysisPhase] = React.useState<AnalysisPhase>('idle');
	const [currentDemoIndex, setCurrentDemoIndex] = React.useState(0);
	const [totalDemos, setTotalDemos] = React.useState(0);
	const [individualAnalyses, setIndividualAnalyses] = React.useState<IndividualAnalysis[]>([]);

	// Refs for tracking state across async operations
	const hasStartedConversationRef = React.useRef<boolean>(false);
	const synthesisConversationIdRef = React.useRef<string | null>(null);

	// Get demo IDs from selected lead search views
	const demoIds = React.useMemo(
		() => selectedLeadSearchViews.map(view => view.company?.id).filter((id): id is string => Boolean(id)),
		[selectedLeadSearchViews]
	);

	// Fetch recordings for all selected demos
	const recordingsQueries = useLeadDemoRecordingQueries({
		demoIds,
		enabled: shouldFetchData && demoIds.length > 0,
		staleTime: 1000 * 60 * 60 * 1, // 1 hour
		meta: {
			toastErrorMessage: 'Failed to load demo recordings.',
			errorLogging: {
				category: 'useMultiDemoSelectionAnalysis',
				action: 'useLeadDemoRecordingQueries',
			},
		},
	});

	// Extract meeting IDs from recordings (only first recording per demo)
	const meetingIds = React.useMemo(() => {
		if (!shouldFetchData) return [];
		const ids: string[] = [];
		recordingsQueries.forEach(query => {
			if (query.data?.values && query.data.values.length > 0) {
				// Only take the first recording per demo
				const firstRecording = query.data.values[0];
				if (firstRecording.uuid) {
					ids.push(firstRecording.uuid);
				}
			}
		});
		return ids;
	}, [recordingsQueries, shouldFetchData]);

	// Fetch transcripts for all meeting IDs
	// Note: We intentionally omit toastErrorMessage here since missing transcripts
	// are handled gracefully in the UI (shown as errors in individual demo analysis)
	const transcriptQueries = useDemoTranscriptQueries(
		meetingIds.map(meetingId => ({
			body: { meetingIds: [meetingId] },
			staleTime: 1000 * 60 * 60 * 1, // 1 hour
			meta: {
				errorLogging: {
					category: 'useMultiDemoSelectionAnalysis',
					action: 'useDemoTranscriptQueries',
				},
			},
		}))
	);

	// Check if all queries are loaded
	const allRecordingsLoaded = recordingsQueries.every(query => query.isSuccess || query.isError);
	const allTranscriptsLoaded = transcriptQueries.every(query => query.isSuccess || query.isError);
	const isLoadingData =
		recordingsQueries.some(query => query.isFetching) || transcriptQueries.some(query => query.isFetching);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
	});

	// Streaming mutation for synthesis phase (streams to UI)
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			conversationId,
			chatRequest,
		}: {
			assistantName: string;
			conversationId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${conversationId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event: any, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: params.conversationId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;

							updatedText = data.isDelta === false ? messageText : updatedText + messageText;

							return [
								...messages,
								{
									...lastAssistantMessage,
									text: updatedText,
								},
							];
						} else {
							return [
								...(messages || []),
								{
									id: data.message.id,
									role: Api.AIChatRole.Assistant,
									text: messageText,
									creationDate: new Date().toISOString(),
								},
							];
						}
					}
				);
			}
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPromptMutation', error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
			setAnalysisPhase('complete');
		},
	});

	/**
	 * Extract formatted transcription for a single demo
	 */
	const getFormattedTranscriptionForDemo = React.useCallback(
		(leadSearchView: ILeadSearchView, recordingIndex: number): string | null => {
			const companyRecordingQuery = recordingsQueries[recordingIndex];
			if (!companyRecordingQuery?.data?.values || companyRecordingQuery.data.values.length === 0) {
				return null;
			}

			// Only use the first recording per demo
			const firstRecording = companyRecordingQuery.data.values[0];

			// Find transcript for the first recording
			const transcriptQuery = transcriptQueries.find(query => {
				return query.data?.some(t => t.meetingId === firstRecording.uuid);
			});
			const transcript = transcriptQuery?.data?.find(t => t.meetingId === firstRecording.uuid);

			const recordingsWithTranscripts = [
				{
					recording: firstRecording,
					transcript,
				},
			];

			return extractDemoContext(leadSearchView, recordingsWithTranscripts);
		},
		[recordingsQueries, transcriptQueries]
	);

	/**
	 * Analyze a single demo and return the analysis text
	 */
	const analyzeIndividualDemo = React.useCallback(
		async (leadSearchView: ILeadSearchView, index: number): Promise<string> => {
			const formattedTranscription = getFormattedTranscriptionForDemo(leadSearchView, index);

			if (!formattedTranscription) {
				throw new Error('No transcript available for this demo');
			}

			// Create a conversation for this individual analysis
			const conversation = await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				source: `${source}-individual-${index}`,
				agentTemplateId,
			});

			// Make a non-streaming request to get the full analysis
			return new Promise<string>((resolve, reject) => {
				let fullResponse = '';

				const handleResponse = (response: Response) => {
					const reader = response.body?.getReader();
					if (!reader) {
						reject(new Error('No response body'));
						return;
					}

					const decoder = new TextDecoder();
					const processStream = async () => {
						try {
							let reading = true;
							while (reading) {
								const { done, value } = await reader.read();
								if (done) {
									reading = false;
									continue;
								}

								const chunk = decoder.decode(value, { stream: true });
								const lines = chunk.split('\n');

								for (const line of lines) {
									if (line.startsWith('data:')) {
										try {
											const jsonStr = line.slice(5).trim();
											if (jsonStr) {
												const data = JSON.parse(jsonStr);
												if (data.message?.text) {
													if (data.isDelta === false) {
														fullResponse = data.message.text;
													} else {
														fullResponse += data.message.text;
													}
												}
											}
										} catch {
											// Skip non-JSON lines
										}
									}
								}
							}
							resolve(fullResponse);
						} catch (error) {
							reject(error);
						}
					};
					processStream();
				};

				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${SALES_DEMO_CALL_COACHING_AGENT}/conversation/${conversation.id}/stream`,
					}),
					'POST',
					{
						chatMessages: [
							{
								role: Api.AIChatRole.User,
								text: `Single Demo Recording:\n${formattedTranscription}`,
								id: null,
							},
						],
					},
					handleResponse,
					reject
				);
			});
		},
		[
			aiAssistantCreateConversationMutation,
			agentTemplateId,
			getFormattedTranscriptionForDemo,
			source,
			userSession.webServiceHelper,
		]
	);

	/**
	 * Process all demos with concurrency control
	 */
	const processAllDemos = React.useCallback(async () => {
		const analyses: IndividualAnalysis[] = selectedLeadSearchViews.map((view, i) => ({
			demoId: view.company?.id || `demo-${i}`,
			demoName: view.company?.companyName || `Demo ${i + 1}`,
			demoDate: view.lastDeal?.demoPerformDate
				? new Date(view.lastDeal.demoPerformDate).toLocaleDateString()
				: undefined,
			analysis: '',
			status: 'pending' as const,
		}));

		setIndividualAnalyses(analyses);
		setTotalDemos(selectedLeadSearchViews.length);
		setAnalysisPhase('individual');

		// Process queue with concurrency limit
		const queue = [...selectedLeadSearchViews.map((view, i) => ({ view, index: i }))];
		let completedCount = 0;

		const processNext = async (): Promise<void> => {
			const item = queue.shift();
			if (!item) return;

			const { view, index } = item;

			// Update status to analyzing
			setIndividualAnalyses(prev => prev.map((a, i) => (i === index ? { ...a, status: 'analyzing' as const } : a)));
			setCurrentDemoIndex(completedCount);

			try {
				const analysis = await analyzeIndividualDemo(view, index);

				// Update with completed analysis
				setIndividualAnalyses(prev =>
					prev.map((a, i) => (i === index ? { ...a, analysis, status: 'complete' as const } : a))
				);
			} catch (error) {
				logger.logApiError('analyzeIndividualDemo', error as Api.IOperationResultNoValue);

				// Mark as error but continue with others
				setIndividualAnalyses(prev =>
					prev.map((a, i) =>
						i === index
							? { ...a, status: 'error' as const, error: error instanceof Error ? error.message : 'Unknown error' }
							: a
					)
				);
			} finally {
				completedCount++;

				// Process next item in queue
				if (queue.length > 0) {
					await processNext();
				}
			}
		};

		// Start initial batch based on concurrency limit
		const initialBatch = Math.min(concurrencyLimit, queue.length);
		const workers = Array(initialBatch)
			.fill(null)
			.map(() => processNext());

		await Promise.all(workers);
	}, [analyzeIndividualDemo, concurrencyLimit, logger, selectedLeadSearchViews]);

	/**
	 * Run the synthesis phase after all individual analyses are complete
	 */
	const runSynthesis = React.useCallback(
		async (analyses: IndividualAnalysis[]) => {
			setAnalysisPhase('synthesis');

			// Create a new conversation for synthesis
			const conversation = await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				source: `${source}-synthesis`,
				agentTemplateId,
			});

			synthesisConversationIdRef.current = conversation.id;

			// Build synthesis prompt
			const synthesisPrompt = buildSynthesisPrompt(analyses);

			// Stream the synthesis response to UI
			aiPromptMutation.mutate({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				conversationId: conversation.id,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: synthesisPrompt,
							id: null,
						},
					],
				},
			});
		},
		[aiAssistantCreateConversationMutation, aiPromptMutation, agentTemplateId, source]
	);

	// When all data is loaded and we haven't started conversation yet
	React.useEffect(() => {
		if (
			shouldFetchData &&
			allRecordingsLoaded &&
			allTranscriptsLoaded &&
			!hasStartedConversationRef.current &&
			!isLoadingData
		) {
			// Mark that we've started
			hasStartedConversationRef.current = true;

			// Open modal
			setIsAnalysisModalOpen(true);

			// If only 1 demo, skip to direct analysis (no synthesis needed)
			if (selectedLeadSearchViews.length === 1) {
				setIsLoadingTranscriptions(true);

				try {
					const formattedTranscription = getFormattedTranscriptionForDemo(selectedLeadSearchViews[0], 0);

					if (!formattedTranscription) {
						errorMessages.push({
							messages: ['No transcript available for this demo.'],
							title: 'No Transcript',
						});
						setIsAnalysisModalOpen(false);
						setShouldFetchData(false);
						hasStartedConversationRef.current = false;
						return;
					}

					setAnalysisPhase('synthesis');
					setTotalDemos(1);
					setIndividualAnalyses([
						{
							demoId: selectedLeadSearchViews[0].company?.id || 'demo-0',
							demoName: selectedLeadSearchViews[0].company?.companyName || 'Demo 1',
							demoDate: selectedLeadSearchViews[0].lastDeal?.demoPerformDate
								? new Date(selectedLeadSearchViews[0].lastDeal.demoPerformDate).toLocaleDateString()
								: undefined,
							analysis: '',
							status: 'analyzing',
						},
					]);

					aiAssistantCreateConversationMutation
						.mutateAsync({
							assistantName: SALES_DEMO_CALL_COACHING_AGENT,
							source,
							agentTemplateId,
						})
						.then(conversation => {
							synthesisConversationIdRef.current = conversation.id;

							aiPromptMutation.mutate({
								assistantName: SALES_DEMO_CALL_COACHING_AGENT,
								conversationId: conversation.id,
								chatRequest: {
									chatMessages: [
										{
											role: Api.AIChatRole.User,
											text: `Single Demo Recording:\n${formattedTranscription}`,
											id: null,
										},
									],
								},
							});
						});
				} catch (error) {
					console.error('Failed to prepare transcription:', error);
					errorMessages.pushApiError(error);
					setIsAnalysisModalOpen(false);
					setShouldFetchData(false);
					hasStartedConversationRef.current = false;
				} finally {
					setIsLoadingTranscriptions(false);
				}
			} else {
				// Multiple demos - use hierarchical approach
				processAllDemos().then(async () => {
					// Get the latest analyses
					setIndividualAnalyses(currentAnalyses => {
						// Check if we have any completed analyses
						const completedAnalyses = currentAnalyses.filter(a => a.status === 'complete');
						if (completedAnalyses.length > 0) {
							// Run synthesis with the analyses we have
							runSynthesis(currentAnalyses);
						} else {
							errorMessages.push({
								messages: ['All individual analyses failed. Unable to create synthesis.'],
								title: 'Analysis Failed',
							});
							setAnalysisPhase('complete');
						}
						return currentAnalyses;
					});
				});
			}
		}
	}, [
		shouldFetchData,
		allRecordingsLoaded,
		allTranscriptsLoaded,
		isLoadingData,
		selectedLeadSearchViews,
		getFormattedTranscriptionForDemo,
		processAllDemos,
		runSynthesis,
		source,
		agentTemplateId,
		aiAssistantCreateConversationMutation,
		aiPromptMutation,
		errorMessages,
	]);

	// Handle critical errors in fetching (only recordings - transcript errors are handled gracefully per-demo)
	React.useEffect(() => {
		const hasRecordingError = recordingsQueries.some(query => query.isError);
		if (hasRecordingError) {
			setIsAnalysisModalOpen(false);
			setShouldFetchData(false);
			setAnalysisPhase('idle');
			hasStartedConversationRef.current = false;
		}
	}, [recordingsQueries]);

	// Start analysis for multiple demos
	const startMultiAnalysis = async () => {
		if (selectedLeadSearchViews.length === 0) {
			errorMessages.push({
				messages: ['Please select at least one demo to analyze.'],
				title: 'No Demos Selected',
			});
			return;
		}

		aiPromptMutation.reset();
		setHasFetchedFeedback(false);
		setIsNewAnalysisSession(true);
		setShouldFetchData(true);
		setAnalysisPhase('idle');
		setCurrentDemoIndex(0);
		setTotalDemos(0);
		setIndividualAnalyses([]);
		hasStartedConversationRef.current = false;
		synthesisConversationIdRef.current = null;
	};

	const closeAnalysis = () => {
		aiPromptMutation.reset();
		setIsAnalysisModalOpen(false);
		setHasFetchedFeedback(false);
		setIsNewAnalysisSession(true);
		setShouldFetchData(false);
		setAnalysisPhase('idle');
		setCurrentDemoIndex(0);
		setTotalDemos(0);
		setIndividualAnalyses([]);
		hasStartedConversationRef.current = false;
		synthesisConversationIdRef.current = null;
	};

	// Reset tracking when starting a new AI session
	React.useEffect(() => {
		if (!hasFetchedFeedback && isNewAnalysisSession) {
			setIsNewAnalysisSession(false);
		}
	}, [hasFetchedFeedback, isNewAnalysisSession]);

	const sendFollowUp = (question: string) => {
		if (!question.trim() || !synthesisConversationIdRef.current) return;

		reactQueryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: synthesisConversationIdRef.current,
			}),
			(messages: Api.AIChatMessage[]) => {
				return [...messages, { role: Api.AIChatRole.User, text: question }];
			}
		);

		// Reset AI response for new streaming response but keep conversation history
		setHasFetchedFeedback(false);

		aiPromptMutation.mutate({
			assistantName: SALES_DEMO_CALL_COACHING_AGENT,
			conversationId: synthesisConversationIdRef.current,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: question,
						id: null,
					},
				],
			},
		});
	};

	return {
		// Original interface (backward compatible)
		isAnalysisModalOpen,
		startMultiAnalysis,
		closeAnalysis,
		isLoading:
			aiAssistantCreateConversationMutation.isPending ||
			aiPromptMutation.isLoading ||
			isLoadingTranscriptions ||
			analysisPhase === 'individual',
		isLoadingTranscripts: isLoadingData || isLoadingTranscriptions,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		sendFollowUp,
		conversationId: synthesisConversationIdRef.current || aiAssistantCreateConversationMutation.data?.id,
		aiPromptMutation,

		// New hierarchical analysis state
		analysisPhase,
		currentDemoIndex,
		totalDemos,
		individualAnalyses,
	};
};
